import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from 'react-redux';
import { useTranslation, Trans } from "react-i18next";

import {
  Card,
  Button,
  Modal
} from '@lockerpm/design';

import {
  RightOutlined,
  DownOutlined,
  CheckOutlined,
  CloseOutlined,
  InfoCircleFilled
} from '@ant-design/icons';

import global from "../../config/global";
import storeActions from "../../store/actions";
import onboardingServices from "../../services/onboarding";

const OnboardingStep = (props) => {
  const  { t } = useTranslation();
  const { step, stt, open, setOpenIndex } = props;
  const disabled = useMemo(() => {
    return step.done || step.disabled
  })
  useEffect(() => {
    if (open, step.done) {
      setOpenIndex(null)
    }
  }, [open, step.done])
  return (
    <div className="flex flex-col gap-1">
      <div
        className={`flex items-center gap-1 ${disabled ? 'text-black-400' : 'cursor-pointer text-black-500'} ${open ? 'text-primary font-semibold' : ''}`}
        onClick={() => disabled ? () => {} : setOpenIndex(open ? null : stt - 1)}
      >
        <span>
          {stt}.
        </span>
        <span>
          {t(`onboarding.steps_card.step${stt}.title`)}
        </span>
        {
          step.done ? <CheckOutlined /> : (open ? <DownOutlined /> : <RightOutlined />)
        }
      </div>
      {
        open && <div className="flex items-center gap-2 pl-4">
          {t(`onboarding.steps_card.step${stt}.description`)}
          <Button
            type="primary"
            ghost
            size="small"
            onClick={() => step.onClick()}
          >
            {t(`onboarding.steps_card.step${stt}.button`)}
          </Button>
        </div>
      }
    </div>
  )
}

const OnboardingModal = (props) => {
  const  { t } = useTranslation();
  const {visible, callingAPI, onOk, onCancel} = props;
  return (
    <Modal
      title={
        <div className="flex items-center gap-3">
          <InfoCircleFilled className="text-warning text-xl"/>
          <p>
            {t('onboarding.steps_card.modal.title')}
          </p>
        </div>
      }
      open={visible}
      width={478}
      okText={t('onboarding.steps_card.modal.ok_btn')}
      cancelText={t('onboarding.steps_card.modal.cancel_btn')}
      closable={false}
      cancelButtonProps={{
        loading: callingAPI,
      }}
      okButtonProps={{
        disabled: callingAPI
      }}
      onOk={() => onOk()}
      onCancel={() => onCancel()}
    >
      <p className="pl-8">
        {t('onboarding.steps_card.modal.description')}
      </p>
    </Modal>
  )
}
 
const Onboarding = (props) => {
  const {
  } = props;

  const projects = useSelector((state) => state.project.projects)
  const selectedWorkspace = useSelector((state) => state.workspace.selectedWorkspace)
  const onboardingProcess = useSelector((state) => state.onboarding.onboardingProcess)
  const changeProcessKey = useSelector((state) => state.onboarding.changeProcessKey)

  const [openIndex, setOpenIndex] = useState(null)
  const [loading, setLoading] = useState(false)
  const [onboardingStatus, setOnboardingStatus] = useState(null)
  const [closeVisible, setCloseVisible] = useState(false)
  const [callingAPI, setCallingAPI] = useState(false)

  const wsProjects = useMemo(() => {
    return projects.filter((p) => p.organization.id == selectedWorkspace?.id)
  }, [projects, selectedWorkspace])

  useEffect(() => {
    fetchOnboarding()
  }, [])

  useEffect(() => {
    updateOnboarding()
  }, [onboardingProcess, changeProcessKey])

  const fetchOnboarding = async(loading = true) => {
    setLoading(loading);
    const res = await onboardingServices.get();
    setOnboardingStatus(res.onboarding_status || "enable");
    global.store.dispatch(storeActions.updateOnboardingProcess(res.onboarding_process));
    setLoading(false);
  }

  const updateOnboarding = async() => {
    if (changeProcessKey && !onboardingProcess[changeProcessKey]) {
      const payload = {
        ...onboardingProcess,
        onboarding_status: onboardingStatus
      };
      payload[changeProcessKey] = true;
      await onboardingServices.update(payload);
      fetchOnboarding(false);
    }
  }

  const steps = useMemo(() => {
    return [
      {
        done: onboardingProcess?.step_1 || false,
        onClick: () => {
          global.navigate('PROJECTS', { workspace_id: selectedWorkspace?.id })
          setTimeout(() => {
            global.store.dispatch(storeActions.updateCreateProject(true));
          }, 100);
          setTimeout(() => {
            global.store.dispatch(storeActions.updateCreateProject(false))
          }, 200);
        }
      },
      {
        done: onboardingProcess?.step_2 || false,
        disabled: wsProjects?.length === 0,
        onClick: () => {
          global.navigate('PROJECT_SECRETS', { workspace_id: selectedWorkspace?.id, project_id: wsProjects[0]?.id })
          setTimeout(() => {
            global.store.dispatch(storeActions.updateInstallTool(true))
          }, 100);
          setTimeout(() => {
            global.store.dispatch(storeActions.updateInstallTool(false))
          }, 200);
        }
      },
      {
        done: onboardingProcess?.step_3 || false,
        disabled: wsProjects?.length === 0,
        onClick: () => {
          global.navigate('PROJECT_SECRETS', { workspace_id: selectedWorkspace?.id, project_id: wsProjects[0]?.id })
          setTimeout(() => {
            global.store.dispatch(storeActions.updateCreateSecret(true))
          }, 100);
          setTimeout(() => {
            global.store.dispatch(storeActions.updateCreateSecret(false))
          }, 200);
        }
      },
      {
        done: onboardingProcess?.step_4 || false,
        disabled: wsProjects?.length === 0,
        onClick: () => {
          global.navigate('PROJECT_ACCESS_KEY', { workspace_id: selectedWorkspace?.id, project_id: wsProjects[0]?.id })
          setTimeout(() => {
            global.store.dispatch(storeActions.updateCreateAccessKey(true))
          }, 100);
          setTimeout(() => {
            global.store.dispatch(storeActions.updateCreateAccessKey(false))
          }, 200);
        }
      }
    ]
  }, [wsProjects, selectedWorkspace, onboardingProcess])

  const isFinished = useMemo(() => {
    return onboardingProcess?.step_1 && onboardingProcess?.step_2 && onboardingProcess?.step_3 && onboardingProcess?.step_4
  }, [onboardingProcess]);

  const handleClose = async () => {
    setCallingAPI(true);
    const payload = {
      ...onboardingProcess,
      onboarding_status: 'disable'
    };
    await onboardingServices.update(payload);
    await fetchOnboarding(false);
    setCallingAPI(false);
  }

  return onboardingStatus !== 'disable' && (
    <div
      style={{ padding: "var(--default-spacing) var(--page-content-spacing)" }}
    >
      <Card
        className={'relative'}
        bodyStyle={{ padding: 20 }}
        loading={loading}
      >
        <div
          className="cursor-pointer absolute top-4 right-5"
          onClick={() => {
            if (isFinished) {
              handleClose();
            } else {
              setCloseVisible(true)
            }
          }}
        >
          <CloseOutlined className="text-black-500 "/>
        </div>
        <div className="flex flex-col gap-3">
          <div>
            <p>
              <Trans
                i18nKey={'onboarding.steps_card.title'}
                components={{
                  b: <b className="font-semibold"/>
                }}
              />
            </p>
          </div>
          <div className="flex flex-col gap-2">
            {
              steps.map((step, index) => <OnboardingStep
                key={index}
                stt={index + 1}
                step={step}
                open={openIndex === index}
                setOpenIndex={setOpenIndex}
              />)
            }
          </div> 
        </div>
      </Card>
      <OnboardingModal
        visible={closeVisible}
        callingAPI={callingAPI}
        onOk={() => setCloseVisible(false)}
        onCancel={() => handleClose()}
      />
    </div>
  );
}

export default Onboarding;